import { useState, useEffect, useCallback } from 'react';
import {
  Router, Download, Loader2, Wifi, WifiOff,
  Network, Shield, Plus, Trash2, Copy, Check, ArrowUpCircle, Printer, KeyRound,
} from 'lucide-react';
import { RouterCredentialsDialog } from '@/components/RouterCredentialsDialog';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import * as provService from '@/services/provisioning';
import type { SiteGateway, SubnetConfig, SubnetSize } from '@asi/shared';

interface GatewayTabProps {
  siteId: string;
  siteName: string;
  accountName?: string;
}

function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Open a print window with the gateway/router sticker */
function printSiteSticker(gateway: SiteGateway, siteName: string, siteId: string, accountName: string) {
  const svgEl = document.querySelector(`[data-qr-gateway-modal="${siteId}"] svg`)
    ?? document.querySelector(`[data-qr-gateway="${siteId}"] svg`);
  const svgHtml = svgEl ? svgEl.outerHTML : '';
  const model = gateway.hardware_model ?? 'MikroTik Router';

  const html = `<!DOCTYPE html>
<html><head><title>Sticker Gateway - ASI Security</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: Arial, sans-serif; display: flex; justify-content: center; padding: 24px; }
  .sticker { border: 2px solid #1a1a2e; border-radius: 12px; padding: 16px 20px; width: 280px; text-align: center; }
  .header-title { font-size: 11px; font-weight: bold; color: #1a1a2e; letter-spacing: 2px; text-transform: uppercase; margin-bottom: 10px; }
  .qr { display: flex; justify-content: center; margin-bottom: 10px; }
  .qr svg { width: 140px; height: 140px; }
  .account { font-size: 10px; color: #555; margin-bottom: 2px; }
  .site-name { font-size: 14px; font-weight: bold; color: #1a1a2e; margin-bottom: 6px; }
  .divider { border: none; border-top: 1px dashed #ccc; margin: 8px 0; }
  .field { display: flex; justify-content: space-between; font-size: 10px; margin-bottom: 3px; padding: 0 4px; }
  .field-label { color: #666; }
  .field-value { font-family: monospace; font-weight: bold; color: #1a1a2e; }
  .instruction { font-size: 9px; color: #888; margin-top: 8px; font-style: italic; }
  .brand { font-size: 10px; font-weight: bold; color: #1a1a2e; margin-top: 6px; letter-spacing: 1px; }
  @media print { body { padding: 0; } }
</style></head>
<body>
  <div class="sticker">
    <div class="header-title">GATEWAY VPN</div>
    <div class="qr">${svgHtml}</div>
    ${accountName ? `<div class="account">${escHtml(accountName)}</div>` : ''}
    <div class="site-name">${escHtml(siteName)}</div>
    <hr class="divider" />
    <div class="field">
      <span class="field-label">Red VPN</span>
      <span class="field-value">${escHtml(gateway.vpn_subnet)}</span>
    </div>
    <div class="field">
      <span class="field-label">Gateway IP</span>
      <span class="field-value">${escHtml(gateway.vpn_gateway_ip)}</span>
    </div>
    <div class="field">
      <span class="field-label">Modelo</span>
      <span class="field-value">${escHtml(model)}</span>
    </div>
    <div class="instruction">Escanea con ASI Security para acceder a este sitio</div>
    <div class="brand">ASI Security</div>
  </div>
</body></html>`;

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow?.document;
  if (!doc) { document.body.removeChild(iframe); return; }
  doc.open();
  doc.write(html);
  doc.close();
  iframe.contentWindow?.focus();
  iframe.contentWindow?.print();
  setTimeout(() => { if (document.body.contains(iframe)) document.body.removeChild(iframe); }, 2000);
}

export function GatewayTab({ siteId, siteName, accountName = '' }: GatewayTabProps) {
  const [gateway, setGateway] = useState<SiteGateway | null>(null);
  const [loading, setLoading] = useState(true);
  const [provisioning, setProvisioning] = useState(false);
  const [sizes, setSizes] = useState<SubnetConfig[]>([]);
  const [selectedSize, setSelectedSize] = useState<SubnetSize>('S');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [rscContent, setRscContent] = useState<string | null>(null);
  const [ec2Config, setEc2Config] = useState<string | null>(null);
  const [rscDialogOpen, setRscDialogOpen] = useState(false);
  const [copied, setCopied] = useState<'rsc' | 'ec2' | null>(null);
  const [resizeOpen, setResizeOpen] = useState(false);
  const [resizeSize, setResizeSize] = useState<SubnetSize>('M');
  const [resizing, setResizing] = useState(false);
  const [configuringEc2, setConfiguringEc2] = useState(false);
  const [stickerOpen, setStickerOpen] = useState(false);
  const [includeWifi, setIncludeWifi] = useState(false);
  const [wifiStepsOpen, setWifiStepsOpen] = useState(false);
  const [credsOpen, setCredsOpen] = useState(false);
  const [routerCode, setRouterCode] = useState<string | null>(null);

  const loadGateway = useCallback(async () => {
    setLoading(true);
    try {
      const gw = await provService.fetchGatewayForSite(siteId);
      setGateway(gw);
    } catch {
      setGateway(null);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    loadGateway();
    provService.fetchSubnetSizes().then(setSizes).catch(() => {});
  }, [loadGateway]);

  const handleProvision = async () => {
    setConfirmOpen(false);
    setProvisioning(true);
    try {
      const result = await provService.provisionGateway(siteId, selectedSize);
      setGateway(result.gateway);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al provisionar');
    } finally {
      setProvisioning(false);
    }
  };

  const handleGenerateRsc = async () => {
    try {
      const result = await provService.generateRsc(siteId, includeWifi);
      setRscContent(result.rsc);
      setEc2Config(result.ec2PeerConfig);
      setRouterCode(result.routerCode ?? null);
      setRscDialogOpen(true);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al generar .rsc');
    }
  };

  const handleDownloadRsc = async () => {
    try {
      await provService.downloadRscFile(siteId, includeWifi);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al descargar .rsc');
    }
  };

  const handleResize = async () => {
    setResizeOpen(false);
    setResizing(true);
    try {
      const result = await provService.resizeGateway(siteId, resizeSize);
      setGateway(result.gateway);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al redimensionar');
    } finally {
      setResizing(false);
    }
  };

  const handleDelete = async () => {
    setDeleteConfirmOpen(false);
    try {
      await provService.deprovisionGateway(siteId);
      setGateway(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al eliminar gateway');
    }
  };

  const handleCopy = async (type: 'rsc' | 'ec2') => {
    const text = type === 'rsc' ? rscContent : ec2Config;
    if (text) {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    }
  };

  const handleConfigureEc2 = async () => {
    setConfiguringEc2(true);
    try {
      const result = await provService.configureEc2Peer(siteId);
      if (result.alreadyConfigured) {
        setGateway((prev) => prev ? { ...prev, ec2_peer_configured: true } : prev);
      } else {
        setGateway((prev) => prev ? { ...prev, ec2_peer_configured: true } : prev);
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al configurar EC2 peer');
    } finally {
      setConfiguringEc2(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ─── No gateway provisioned yet ────────────────────────────

  if (!gateway) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <Router className="h-12 w-12 mb-4 opacity-30" />
          <p className="text-lg font-medium mb-1">Sin gateway configurado</p>
          <p className="text-sm mb-6 text-center max-w-md">
            Provisiona un gateway MikroTik para conectar cámaras, NVRs y paneles
            de alarma a la plataforma ASI vía VPN.
          </p>

          <Card className="w-full max-w-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Provisionar Gateway</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">
                  Tamaño de subred
                </label>
                <Select
                  value={selectedSize}
                  onValueChange={(v) => setSelectedSize(v as SubnetSize)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sizes.map((s) => (
                      <SelectItem key={s.size} value={s.size}>
                        <span className="font-mono mr-2">{s.size}</span>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1.5">
                  {sizes.find((s) => s.size === selectedSize)
                    ? `/${sizes.find((s) => s.size === selectedSize)!.prefix} — ${sizes.find((s) => s.size === selectedSize)!.usableIps} IPs disponibles`
                    : ''}
                </p>
              </div>

              <Button
                className="w-full"
                onClick={() => setConfirmOpen(true)}
                disabled={provisioning}
              >
                {provisioning ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                {provisioning ? 'Provisionando...' : 'Provisionar'}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Confirm dialog */}
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmar provisioning</DialogTitle>
              <DialogDescription>
                Se generarán claves WireGuard y se asignará una subred{' '}
                <span className="font-mono font-medium">{selectedSize}</span>{' '}
                al sitio <span className="font-medium">{siteName}</span>.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleProvision}>Confirmar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ─── Gateway provisioned — show details ────────────────────

  const buildGatewayStickerHtml = (): string => {
    const svgEl = document.querySelector(`[data-qr-gateway-modal="${siteId}"] svg`);
    const svgHtml = svgEl ? svgEl.outerHTML : '';
    const model = gateway.hardware_model ?? 'MikroTik Router';
    return `<!DOCTYPE html>
<html><head><title>Sticker Gateway - ASI Security</title><meta charset="utf-8" /><style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: Arial, sans-serif; display: flex; justify-content: center; padding: 24px; }
  .sticker { border: 2px solid #1a1a2e; border-radius: 12px; padding: 16px 20px; width: 280px; text-align: center; }
  .header-title { font-size: 11px; font-weight: bold; color: #1a1a2e; letter-spacing: 2px; text-transform: uppercase; margin-bottom: 10px; }
  .qr { display: flex; justify-content: center; margin-bottom: 10px; }
  .qr svg { width: 140px; height: 140px; }
  .account { font-size: 10px; color: #555; margin-bottom: 2px; }
  .site-name { font-size: 14px; font-weight: bold; color: #1a1a2e; margin-bottom: 6px; }
  .divider { border: none; border-top: 1px dashed #ccc; margin: 8px 0; }
  .field { display: flex; justify-content: space-between; font-size: 10px; margin-bottom: 3px; padding: 0 4px; }
  .field-label { color: #666; }
  .field-value { font-family: monospace; font-weight: bold; color: #1a1a2e; }
  .instruction { font-size: 9px; color: #888; margin-top: 8px; font-style: italic; }
  .brand { font-size: 10px; font-weight: bold; color: #1a1a2e; margin-top: 6px; letter-spacing: 1px; }
  @media print { body { padding: 0; } }
</style></head><body>
  <div class="sticker">
    <div class="header-title">GATEWAY VPN</div>
    <div class="qr">${svgHtml}</div>
    ${accountName ? `<div class="account">${escHtml(accountName)}</div>` : ''}
    <div class="site-name">${escHtml(siteName)}</div>
    <hr class="divider" />
    <div class="field"><span class="field-label">Red VPN</span><span class="field-value">${escHtml(gateway.vpn_subnet)}</span></div>
    <div class="field"><span class="field-label">Gateway IP</span><span class="field-value">${escHtml(gateway.vpn_gateway_ip)}</span></div>
    <div class="field"><span class="field-label">Modelo</span><span class="field-value">${escHtml(model)}</span></div>
    <div class="instruction">Escanea con ASI Security para acceder a este sitio</div>
    <div class="brand">ASI Security</div>
  </div>
</body></html>`;
  };

  const statusColor: Record<string, string> = {
    online: 'bg-green-500/10 text-green-500 border-green-500/20',
    offline: 'bg-red-500/10 text-red-500 border-red-500/20',
    error: 'bg-red-500/10 text-red-500 border-red-500/20',
  };

  const statusLabel: Record<string, string> = {
    provisioned: 'Provisionado',
    online: 'En línea',
    offline: 'Fuera de línea',
    error: 'Error',
  };

  return (
    <div className="space-y-5">
      {/* WiFi Configuration Option */}
      <Card className={`border transition-colors ${includeWifi ? 'border-blue-500/40 bg-blue-500/5' : ''}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${includeWifi ? 'bg-blue-500/20' : 'bg-muted'}`}>
                <Wifi className={`h-4 w-4 ${includeWifi ? 'text-blue-500' : 'text-muted-foreground'}`} />
              </div>
              <div>
                <p className="text-sm font-medium">WiFi en el .rsc</p>
                <p className="text-xs text-muted-foreground">
                  {includeWifi
                    ? 'Se incluirá: SSID oculto "ASI-Gateway" · WPA2 · clave propia del router'
                    : 'No se incluirá configuración inalámbrica'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIncludeWifi((v) => !v)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                includeWifi ? 'bg-blue-500' : 'bg-input'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                  includeWifi ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          {includeWifi && (
            <div className="mt-3 pt-3 border-t border-border grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">SSID</span>
                <span className="font-mono font-medium">ASI-Gateway</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Visibilidad</span>
                <span className="font-medium text-amber-500">Oculto</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Seguridad</span>
                <span className="font-mono font-medium">WPA2-PSK</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Bridge</span>
                <span className="font-mono font-medium">bridge-lan</span>
              </div>
              <div className="col-span-2 text-muted-foreground/70 italic text-[10px] mt-0.5">
                El .rsc incluye comandos para serie ax (wifi1) con alternativa legacy comentada (wlan1).
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Status header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {gateway.status === 'online' ? (
            <Wifi className="h-5 w-5 text-green-500" />
          ) : (
            <WifiOff className="h-5 w-5 text-muted-foreground" />
          )}
          <div>
            <p className="text-sm font-medium">Gateway VPN</p>
            <Badge variant="outline" className={statusColor[gateway.status] ?? ''}>
              {statusLabel[gateway.status] ?? gateway.status}
            </Badge>
          </div>
        </div>

        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setStickerOpen(true)} title="Ver / imprimir sticker del router">
            <Printer className="h-4 w-4 mr-1.5" />
            Sticker
          </Button>
          <Button size="sm" variant="outline" onClick={() => setCredsOpen(true)} title="Contraseña admin y WiFi de este router">
            <KeyRound className="h-4 w-4 mr-1.5" />
            Credenciales
          </Button>
          <Button size="sm" variant="outline" onClick={() => setResizeOpen(true)} disabled={resizing}>
            {resizing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ArrowUpCircle className="h-4 w-4 mr-1.5" />}
            Redimensionar
          </Button>
          <Button size="sm" variant="outline" onClick={handleGenerateRsc}>
            <FileIcon className="h-4 w-4 mr-1.5" />
            Ver .rsc
          </Button>
          <Button size="sm" variant="outline" onClick={handleDownloadRsc}>
            <Download className="h-4 w-4 mr-1.5" />
            Descargar .rsc
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
            onClick={() => setDeleteConfirmOpen(true)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Network info */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Network className="h-4 w-4" />
              Red VPN
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subred</span>
              <span className="font-mono">{gateway.vpn_subnet}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Gateway IP</span>
              <span className="font-mono">{gateway.vpn_gateway_ip}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">EC2 Peer IP</span>
              <span className="font-mono">{gateway.ec2_peer_ip}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Shield className="h-4 w-4" />
              WireGuard
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Public Key</span>
              <span className="font-mono text-xs truncate max-w-[180px]" title={gateway.wg_public_key}>
                {gateway.wg_public_key.substring(0, 20)}...
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">EC2 Peer</span>
              <Badge variant={gateway.ec2_peer_configured ? 'default' : 'secondary'}>
                {gateway.ec2_peer_configured ? 'Configurado' : 'Pendiente'}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Provisionado</span>
              <span>{gateway.provisioned_at ? new Date(gateway.provisioned_at).toLocaleDateString('es-GT') : '—'}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Hardware info */}
      {(gateway.hardware_model || gateway.hardware_serial) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Router className="h-4 w-4" />
              Hardware
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {gateway.hardware_model && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Modelo</span>
                <span>{gateway.hardware_model}</span>
              </div>
            )}
            {gateway.hardware_serial && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Serial</span>
                <span className="font-mono">{gateway.hardware_serial}</span>
              </div>
            )}
            {gateway.firmware_version && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Firmware</span>
                <span>{gateway.firmware_version}</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* EC2 config pending banner */}
      {!gateway.ec2_peer_configured && (
        <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-600 mb-1">
                Peer EC2 pendiente de configurar
              </p>
              <p className="text-xs text-muted-foreground">
                El peer WireGuard se agregará automáticamente al servidor EC2.
              </p>
            </div>
            <Button
              size="sm"
              onClick={handleConfigureEc2}
              disabled={configuringEc2}
            >
              {configuringEc2 ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Shield className="h-4 w-4 mr-1.5" />
              )}
              {configuringEc2 ? 'Configurando...' : 'Configurar EC2'}
            </Button>
          </div>
        </div>
      )}

      {/* RSC Content Dialog */}
      <Dialog open={rscDialogOpen} onOpenChange={setRscDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Configuración Gateway</DialogTitle>
            <DialogDescription>
              Archivo .rsc para importar en MikroTik y configuración peer para EC2.
            </DialogDescription>
          </DialogHeader>

          {/* ── Scrollable body ─────────────────────────────── */}
          <div className="flex-1 overflow-y-auto space-y-3 pr-1">

          {/* Import instructions */}
          <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-3 text-xs space-y-1">
            <p className="font-medium text-blue-600">Pasos para importar en MikroTik:</p>
            <ol className="list-decimal list-inside space-y-0.5 text-muted-foreground">
              <li>Resetear router: <code className="text-xs">System &gt; Reset Configuration</code> → marcar <strong>No Default Configuration</strong></li>
              <li>Conectar via <strong>MAC-WinBox</strong> (pestaña Neighbors)</li>
              <li>Arrastrar el archivo .rsc a <strong>Files</strong> en WinBox</li>
              <li>Abrir <strong>New Terminal</strong> y ejecutar: <code className="text-xs bg-muted px-1 rounded">/import file-name=NOMBRE.rsc</code></li>
              <li>Esperar a que termine y reiniciar</li>
              <li>Verificar en <strong>Files</strong> que el .rsc ya no esté y borrar la copia de la PC (contiene contraseñas)</li>
            </ol>
            {routerCode && (
              <p className="text-muted-foreground">
                Código del router: <code className="font-mono font-medium">{routerCode}</code> — la contraseña de admin es propia
                de este router (botón <strong>Credenciales</strong>).
              </p>
            )}
            <p className="text-muted-foreground/70 italic">No usar &quot;Run After Reset&quot; ni &quot;Restore&quot; — solo /import desde terminal.</p>
          </div>

          {/* WiFi manual steps — solo si se activó la opción, collapsible */}
          {includeWifi && (
            <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 text-xs overflow-hidden">
              <button
                type="button"
                onClick={() => setWifiStepsOpen((v) => !v)}
                className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-yellow-500/10 transition-colors"
              >
                <span className="font-medium text-yellow-600">WiFi — ejecutar manualmente después del import</span>
                <svg className={`h-3.5 w-3.5 text-yellow-600 transition-transform ${wifiStepsOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {wifiStepsOpen && (
                <div className="px-3 pb-3 pt-1 space-y-2 border-t border-yellow-500/20">
                  <p className="text-muted-foreground">Verificar paquete: <code className="bg-muted px-1 rounded">/system package print</code></p>
                  <p className="text-muted-foreground">
                    Reemplazar <code className="bg-muted px-1 rounded">CLAVE-WIFI</code> por la clave WiFi de este router (botón <strong>Credenciales</strong>).
                  </p>
                  <div className="space-y-1">
                    <p className="font-medium text-muted-foreground">Paquete <code className="bg-muted px-1 rounded">wifi</code> (hAP ax2/ax3, cAP ax):</p>
                    <pre className="bg-muted rounded p-1.5 text-[10px] leading-relaxed select-all whitespace-pre-wrap">{`/interface wifi set wifi1 configuration.ssid="ASI-Gateway" configuration.hide-ssid=yes security.authentication-types=wpa2-psk security.passphrase="CLAVE-WIFI" disabled=no
/interface bridge port add bridge=bridge-lan interface=wifi1 comment="WiFi camaras"`}</pre>
                  </div>
                  <div className="space-y-1">
                    <p className="font-medium text-muted-foreground">Paquete <code className="bg-muted px-1 rounded">wireless</code> (hAP ac2/ac3, hAP lite):</p>
                    <pre className="bg-muted rounded p-1.5 text-[10px] leading-relaxed select-all whitespace-pre-wrap">{`/interface wireless security-profiles add name=asi-wifi authentication-types=wpa2-psk wpa2-pre-shared-key="CLAVE-WIFI" mode=dynamic-keys
/interface wireless set wlan1 ssid="ASI-Gateway" security-profile=asi-wifi hide-ssid=yes mode=ap-bridge disabled=no
/interface bridge port add bridge=bridge-lan interface=wlan1 comment="WiFi camaras"`}</pre>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Credentials — per router, fetched on demand */}
          <div className="rounded-lg border border-muted bg-muted/30 text-xs px-3 py-2 flex items-center justify-between gap-3">
            <span className="text-muted-foreground">
              Contraseña admin y clave WiFi propias de este router{routerCode ? <> (<code className="font-mono">{routerCode}</code>)</> : null}.
            </span>
            <Button size="sm" variant="outline" onClick={() => setCredsOpen(true)}>
              <KeyRound className="h-3.5 w-3.5 mr-1.5" />
              Credenciales
            </Button>
          </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium">MikroTik .rsc</h4>
                <Button size="sm" variant="ghost" onClick={() => handleCopy('rsc')}>
                  {copied === 'rsc' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
              </div>
              <pre className="bg-muted rounded-lg p-3 text-xs font-mono overflow-x-auto max-h-60 whitespace-pre">
                {rscContent}
              </pre>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium">EC2 WireGuard Peer</h4>
                <Button size="sm" variant="ghost" onClick={() => handleCopy('ec2')}>
                  {copied === 'ec2' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
              </div>
              <pre className="bg-muted rounded-lg p-3 text-xs font-mono overflow-x-auto max-h-40 whitespace-pre">
                {ec2Config}
              </pre>
            </div>

          </div>{/* end scrollable body */}

          <DialogFooter>
            <Button variant="outline" onClick={() => setRscDialogOpen(false)}>
              Cerrar
            </Button>
            <Button onClick={handleDownloadRsc}>
              <Download className="h-4 w-4 mr-2" />
              Descargar .rsc
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resize dialog */}
      <Dialog open={resizeOpen} onOpenChange={setResizeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Redimensionar subred</DialogTitle>
            <DialogDescription>
              Subred actual: <span className="font-mono font-medium">{gateway.vpn_subnet}</span>.
              Se asignará una nueva subred más grande y deberás re-importar el .rsc en el MikroTik.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium mb-1.5 block">Nuevo tamaño</label>
            <Select
              value={resizeSize}
              onValueChange={(v) => setResizeSize(v as SubnetSize)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sizes
                  .filter((s) => {
                    const currentPrefix = parseInt(gateway.vpn_subnet.split('/')[1], 10);
                    return s.prefix < currentPrefix; // Only show larger sizes
                  })
                  .map((s) => (
                    <SelectItem key={s.size} value={s.size}>
                      <span className="font-mono mr-2">{s.size}</span>
                      {s.label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResizeOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleResize}>
              <ArrowUpCircle className="h-4 w-4 mr-2" />
              Redimensionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gateway Sticker Modal */}
      <Dialog open={stickerOpen} onOpenChange={(o) => !o && setStickerOpen(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Sticker del gateway</DialogTitle>
            <DialogDescription>Vista previa — imprime o descarga como archivo HTML.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-4">
            <div className="border-2 border-[#1a1a2e] rounded-xl p-4 w-64 text-center">
              <div className="text-[11px] font-bold tracking-[2px] uppercase text-[#1a1a2e] mb-2">
                GATEWAY VPN
              </div>
              <div className="flex justify-center mb-2" data-qr-gateway-modal={siteId}>
                <QRCodeSVG value={`asi-site://${siteId}`} size={140} level="M" includeMargin={false} />
              </div>
              {accountName && (
                <div className="text-[10px] text-gray-500 truncate">{accountName}</div>
              )}
              <div className="text-sm font-bold text-[#1a1a2e] mb-1">{siteName}</div>
              <hr className="border-dashed border-gray-300 my-1.5" />
              <div className="text-[10px] space-y-1 text-left px-1">
                <div className="flex justify-between">
                  <span className="text-gray-500">Red VPN</span>
                  <span className="font-mono font-bold text-[#1a1a2e] text-[9px]">{gateway.vpn_subnet}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Gateway IP</span>
                  <span className="font-mono font-bold text-[#1a1a2e] text-[9px]">{gateway.vpn_gateway_ip}</span>
                </div>
                {gateway.hardware_model && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Modelo</span>
                    <span className="font-mono font-bold text-[#1a1a2e] text-[9px]">{gateway.hardware_model}</span>
                  </div>
                )}
              </div>
              <div className="text-[9px] text-gray-400 italic mt-2">
                Escanea con ASI Security para acceder
              </div>
              <div className="text-[10px] font-bold text-[#1a1a2e] mt-1 tracking-wider">
                ASI Security
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                const html = buildGatewayStickerHtml();
                const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `sticker-gateway-${siteName.replace(/\s+/g, '-').toLowerCase()}.html`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              }}
            >
              <Download className="h-4 w-4 mr-2" />
              Descargar
            </Button>
            <Button
              onClick={() => {
                printSiteSticker(gateway, siteName, siteId, accountName);
              }}
            >
              <Printer className="h-4 w-4 mr-2" />
              Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RouterCredentialsDialog open={credsOpen} onOpenChange={setCredsOpen} lookup={gateway.id} />

      {/* Delete confirmation */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar gateway</DialogTitle>
            <DialogDescription>
              Se eliminará la configuración del gateway para este sitio.
              Las claves WireGuard se perderán y deberás re-provisionar.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Small file icon since lucide doesn't have a "FileScript" icon
function FileIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
      <path d="m10 13-2 2 2 2" />
      <path d="m14 17 2-2-2-2" />
    </svg>
  );
}
